import { ChangeDetectorRef, Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { finalize } from 'rxjs';

import { AlertService, Logger, ModalManager, NavigationService } from '@app/services';
import { ActionsAnalytics, CategoriesAnalytics } from '@app/services/analytics/models/analytics.enum';
import { ErrorMsg, ErrorTypes } from '@app/services/error/error.model';
import { ManageVacationsService } from '@app/services/manage-vacations/manage-vacations.service';
import { UserService } from '@app/services/user/user.service';
import { BasePageDirective } from '@app/shared/bases/base.page';
import { PAGES } from '@app/shared/enums/pages/pages.enum';
import { getUrlFromMap } from '@app/shared/enums/pages/pages.urls';
import { Permissions } from '@app/shared/enums/permissions/permissions.enum';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep } from 'lodash';

import { SEASONS } from './manage-vacations.constants';
import { IEmployeeObservations, IProposal, UpdateProposal } from './manage-vacations.interface';
import {
  MomoSeasonProposal,
  MomoVacationsCriteria,
  MomoVacationsProposal,
  MotSeasonProposal,
  MotVacationsCriteria,
  SeasonCalendar,
  SeasonProposal,
  VacationsProposal,
  VacationsSeason
} from './manage-vacations.model';

@Component({
  selector: 'app-manage-vacations',
  templateUrl: './manage-vacations.page.html',
  styleUrls: ['./manage-vacations.page.scss']
})
export class BaseManageVacationsPage extends BasePageDirective implements OnInit, OnDestroy {
  protected logger = new Logger('ManageVacationsPage');

  criteria!: MotVacationsCriteria | MomoVacationsCriteria;
  seasons!: VacationsSeason[];
  proposals!: VacationsProposal | MomoVacationsProposal;
  seasonCalendar!: SeasonCalendar;
  private summerCalendar!: SeasonCalendar;
  private winterCalendar!: SeasonCalendar;
  slots: IProposal[] = [];
  initialSlots: IProposal[] = [];
  modifyingOrder!: number;
  currentStep = 1;
  initialProposals!: VacationsProposal | MomoVacationsProposal;

  serverError!: boolean;
  sectionMaintenance!: boolean;
  seasonProposal!: MotSeasonProposal | MomoSeasonProposal;
  errorMsg!: ErrorMsg;
  loadingData = true;
  selectedSeason!: VacationsSeason;
  notSelectedSeason!: VacationsSeason | undefined;
  hasEditPermission = false;
  backDisabled = true;
  nextDisabled = true;
  backButtonText = 'STEPPER.BACK';
  nextButtonText = 'STEPPER.NEXT';
  showResumeView = false;
  showInfoIcon = false;
  isCalendarView = false;
  isMomoUser = false;

  messages!: string[];

  readonly manageVacationsService: ManageVacationsService = this.injector.get(ManageVacationsService);
  readonly userService: UserService = this.injector.get(UserService);
  readonly modalManager: ModalManager = this.injector.get(ModalManager);
  readonly alertService: AlertService = this.injector.get(AlertService);
  readonly translateService: TranslateService = this.injector.get(TranslateService);
  readonly navigationService: NavigationService = this.injector.get(NavigationService);
  readonly cdr: ChangeDetectorRef = this.injector.get(ChangeDetectorRef);

  constructor(injector: Injector) {
    super(injector);
    this.getTranslations();
    this.initializeErrorHandling();

    this.translateService.onLangChange.subscribe(async () => {
      this.getTranslations();

      if (this.isMomoUser) {
        if (this.currentStep === 1) {
          this.getCriteria();
        } else if (this.currentStep === 2 || this.currentStep === 3) {
          this.getCriteria();
          this.getCalendar(true);
        }
        return;
      }

      if (this.currentStep === 1) {
        await this.getProposals();

        if (this.router.url === `${getUrlFromMap(PAGES.VACATIONS_REQUEST)}/edit`) {
          this.manageVacationsService.currentEditingProposal.next(this.proposals[this.selectedSeason.id]);
        }
      } else if (this.currentStep === 2) {
        this.getCriteria();
      } else if (this.currentStep === 3) {
        this.getCriteria();
        this.getCalendar(true);
      }
    });
  }

  ngOnInit() {
    this.checkUserPermissions();
  }

  ngOnDestroy() {
    this.slots = [];
    this.initialSlots = [];
    this.showResumeView = false;
    this.modifyingOrder = 0;
    this.currentStep = 1;
  }

  get gridFixedStyles() {
    let gridFixed = false;

    if (this.isMomoUser) {
      gridFixed = this.currentStep === 1 || this.currentStep === 4 || this.showResumeView;
    } else {
      gridFixed = this.currentStep !== 3 || this.showResumeView;
    }

    return gridFixed;
  }

  editObservations(observations: IEmployeeObservations) {
    this.manageVacationsService.updateObservations(observations, this.isMomoUser).subscribe({
      next: (response) => {
        this.proposals.observations = this.isMomoUser ? response.content : response;

        this.alertService.showSuccess(
          this.messages['PROFILE.SUCCESS_TITLE'],
          observations.content
            ? this.messages['MANAGE_VACATIONS.INVIDUAL_OBSERVATIONS_EDIT_SUCCESS']
            : this.messages['MANAGE_VACATIONS.INVIDUAL_OBSERVATIONS_DELETE_SUCCESS']
        );
      },
      error: () =>
        this.alertService.showError(
          this.messages['ERRORS_TOASTS.GENERIC_MSG'],
          this.messages['ERROR_MESSAGES.GENERIC_FAIL_MESSAGE']
        )
    });
  }

  setSelectedSeasonByProposal(seasonProposal: SeasonProposal) {
    if (seasonProposal.id === SEASONS.summer) {
      this.selectedSeason = new VacationsSeason({
        id: SEASONS.summer,
        title: this.messages['MANAGE_VACATIONS.SUMMER_VACATIONS']
      });
      this.notSelectedSeason = new VacationsSeason({
        id: SEASONS.winter,
        title: this.messages['MANAGE_VACATIONS.WINTER_VACATIONS']
      });
    } else {
      this.selectedSeason = new VacationsSeason({
        id: SEASONS.winter,
        title: this.messages['MANAGE_VACATIONS.WINTER_VACATIONS']
      });
      this.notSelectedSeason = new VacationsSeason({
        id: SEASONS.summer,
        title: this.messages['MANAGE_VACATIONS.SUMMER_VACATIONS']
      });
    }
  }

  private async checkUserPermissions() {
    const permissions = await this.userService.getPermissions();
    this.isMomoUser =
      permissions.includes(Permissions.MOMO_VACATIONS_PROPOSAL_LIST) ||
      permissions.includes(Permissions.MOMO_VACATIONS_PROPOSAL_EDIT);
    this.hasEditPermission =
      permissions.includes(Permissions.MOT_VACATIONS_PROPOSAL_EDIT) ||
      permissions.includes(Permissions.MOMO_VACATIONS_PROPOSAL_EDIT);
    this.getProposals();
  }

  private getProposals() {
    this.loadingData = true;
    return new Promise<void>((resolve) => {
      this.manageVacationsService
        .getProposals(this.isMomoUser)
        .pipe(
          finalize(() => {
            this.loadingData = false;
            resolve();
          })
        )
        .subscribe((response) => {
          if (response) {
            this.setProposalsData(response);
          } else if (this.hasEditPermission) {
            this.getCriteria();
          }
        });
    });
  }

  getCriteria() {
    this.loadingData = true;
    return new Promise<void>((resolve) => {
      this.manageVacationsService
        .getVacationsCriteria(this.isMomoUser)
        .pipe(
          finalize(() => {
            this.loadingData = false;
            resolve();
          })
        )
        .subscribe((criteria) => {
          this.criteria = criteria;
          this.seasons = this.criteria.seasons;
        });
    });
  }

  private getCalendar(force = false) {
    if (this.hasCalendarStored() && !force) {
      this.resetCalendar();
      return;
    }

    this.loadingData = true;
    this.cdr.detectChanges();

    this.manageVacationsService
      .getSeasonCalendar(this.selectedSeason.id, this.isMomoUser)
      .pipe(finalize(() => (this.loadingData = false)))
      .subscribe((calendar) => {
        this.storeCalendar(calendar);
        this.resetCalendar();
      });
  }

  onCreateProposal(proposalsObject: UpdateProposal) {
    this.manageVacationsService.createProposal(proposalsObject.proposals, this.isMomoUser).subscribe(
      (response) => {
        this.setProposalsData(response);
        this.resetToInitial();

        this.analyticsService.sendEvent(CategoriesAnalytics.VACATIONS_PROPOSAL, {
          [ActionsAnalytics.CLICKACTION]: this.proposals.userHasData() ? 'editar' : 'enviar'
        });

        this.alertService.showSuccess(
          this.messages['PROFILE.SUCCESS_TITLE'],
          proposalsObject.deleting
            ? this.messages['MANAGE_VACATIONS.DELETE_PROPOSAL_SUCCESS']
            : this.messages['MANAGE_VACATIONS.SEND_PROPOSAL_SUCCESS']
        );

        if (this.isMomoUser) {
          this.currentStep = 4;
        }
      },
      (err) => {
        this.proposals = cloneDeep(this.initialProposals);
        this.alertService.showError(this.messages['ERRORS_TOASTS.GENERIC_MSG'], err.error.message);
      }
    );
  }

  private setProposalsData(proposals: VacationsProposal) {
    this.proposals = proposals;

    if (this.isMomoUser) {
      this.currentStep = 4;
    } else {
      this.proposals.setSummerValues();
      this.proposals.setWinterValues();
      this.proposals.setMomoUser(this.isMomoUser);
    }

    this.initialProposals = cloneDeep(this.proposals);
  }

  resetCalendar() {
    this.seasonCalendar = cloneDeep(
      this.selectedSeason.id === SEASONS.winter ? this.winterCalendar : this.summerCalendar
    );
  }

  private storeCalendar(calendar: SeasonCalendar) {
    if (this.selectedSeason.id === SEASONS.summer) {
      this.summerCalendar = calendar;
    } else {
      this.winterCalendar = calendar;
    }
  }

  private hasCalendarStored() {
    return (
      (this.winterCalendar && this.selectedSeason.id === SEASONS.winter) ||
      (this.summerCalendar && this.selectedSeason.id === SEASONS.summer)
    );
  }

  private resetToInitial() {
    this.initialSlots = [];
    this.slots = [];
    this.modifyingOrder = 0;
    this.showResumeView = false;
    this.currentStep = 1;
  }

  loadThirdStep(editing: boolean) {
    if (!editing && !this.isMomoUser) {
      this.seasonProposal = new MotSeasonProposal([]);
      this.seasonProposal.setValues(this.selectedSeason.id);
      this.seasonProposal.setMomoUser(this.isMomoUser);
    }

    this.getCalendar();
  }

  /**
   * This method is used subscribe to possible errors on backend request
   */
  private initializeErrorHandling() {
    this.manageVacationsService.onError$.subscribe((errorType) => {
      switch (errorType) {
        case ErrorTypes.SECTION_MAINTENANCE:
          this.sectionMaintenance = true;
          break;
        case ErrorTypes.EMPTY_SLOTS:
          this.manageVacationsService.errorMsg$.subscribe((errorMsg) => (this.errorMsg = errorMsg));
          break;
        default:
          this.serverError = true;
          break;
      }
    });
  }

  private getTranslations() {
    this.translateService
      .get([
        'PROFILE.SUCCESS_TITLE',
        'MANAGE_VACATIONS.SEND_PROPOSAL_SUCCESS',
        'MANAGE_VACATIONS.DELETE_PROPOSAL_SUCCESS',
        'MANAGE_VACATIONS.WINTER_VACATIONS',
        'MANAGE_VACATIONS.SUMMER_VACATIONS',
        'MANAGE_VACATIONS.INVIDUAL_OBSERVATIONS_EDIT_SUCCESS',
        'MANAGE_VACATIONS.INVIDUAL_OBSERVATIONS_DELETE_SUCCESS',
        'MANAGE_VACATIONS.SLOT_ALREADY_ADDED',
        'MANAGE_VACATIONS.SELECT_ANOTHER_WINTER_SLOT',
        'ERRORS_TOASTS.GENERIC_MSG',
        'INFO_TOASTS.INFO_TITLE',
        'ERROR_MESSAGES.GENERIC_FAIL_MESSAGE'
      ])
      .subscribe((messages) => (this.messages = messages));
  }
}
